/**
 * Format a multi-line string into html with <br> tags
 * @param {String} str | The string to format with <br> tags
 * @param {Boolean} is_xhtml | whether this is xhtml
 */
export const nl2br = (str, is_xhtml) => {
    if (typeof str === 'undefined' || str === null) {
        return '';
    }
    var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';
    return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
}

/**
 * Sanitize and encode all HTML in a user-submitted string
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {String} str | The user-submitted string
 */
export const sanitizeHTML = (str) => {
    var temp = document.createElement('div');
    temp.textContent = str;
    return temp.innerHTML;
};

/**
 * Get an array of supported browser features or check for a specified feature
 * @param {String} ?feature | Optional feature to test
 */
export const getBrowserFeatures = (feature) => {

    let features = []

    // touch support
    if ( 
        ('ontouchstart' in window) 
        || window.TouchEvent 
        || (window.DocumentTouch && window.document instanceof window.DocumentTouch)
    ) {
        features.push('touchevents')
    } else {
        features.push('notouchevents')
    }

    if ( feature ) {
        return features.indexOf(feature) > -1
    }

    return features
  
}

/**
 * Scroll an element to a given position; smoothly if possible.
 * @param {Element} ref | The reference element
 * @param {Number} top | The top position to scroll to
 * @param {Number} left | The left position to scroll to
 */
export const doScrollRef = (ref, top, left) => {
    if (
        ref.scrollTo
        && typeof ref.scrollTo === 'function'
    ) {
        ref.scrollTo({ top, left, behavior: 'smooth' })
    } 
    
    else {
        ref.scrollTop = top
        ref.scrollLeft = left
    }
}

/**
 * Generate a random unique ID string
 */
export const generateUID = () => {
    // I generate the UID from two parts here
    // to ensure the random number provide enough bits.
    var firstPart = (Math.random() * 46656) | 0;
    var secondPart = (Math.random() * 46656) | 0;
    firstPart = ("000" + firstPart.toString(36)).slice(-3);
    secondPart = ("000" + secondPart.toString(36)).slice(-3);
    return firstPart + secondPart;
}

/**
 * Get the INDEX of an object from an array of objects
 * @param {String} prop | The property to search by
 * @param {String|Number|Boolean} value | The value to look for
 * @param {Array} collection | The array of objects to search
 */
export const getItemIndexBy = (prop, value, collection) => {
    return collection.reduce((acc, item, i) => {
        if (
            item instanceof Object 
            && item.hasOwnProperty(prop) 
            && item[prop] === value
        ) {
            return i
        } else {
            return acc
        }
    }, undefined)
}

/**
 * Find an item from an array
 * @param {String} prop | The property to search by
 * @param {String|Number|Boolean} value | The value to look for
 * @param {Array} collection | The array to search
 * @param {Any} withKey | Truthy value to also return items key
 */
export const getItemBy = ( prop, value, collection, withKey ) => {
    let key = -1
    let filtered = []
    if ( typeof collection === 'object' && typeof collection.filter === 'function' && collection.length > 0 ){
        filtered = collection.filter( (item, i) => {
            if (value === item[prop]) {
                key = i
                return true
            } else {
                return false
            }
        } )
    }
    if ( withKey ) {
        return { item: filtered[0], key }
    }
    else {
        return filtered[0]
    }
}

/**
 * Check if 'localStorage' or 'sessionStorage' is supported AND available
 * @param type {string} - 'localStorage' || 'sessionStorage'
 */
export const storageAvailable = (type) => {
    type = type || 'localStorage'
    var storage;
    try {
        storage = window[type];
        var x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    }
    catch(e) {
        return e instanceof DOMException && (
            // everything except Firefox
            e.code === 22 ||
            // Firefox
            e.code === 1014 ||
            // test name field too, because code might not be present
            // everything except Firefox
            e.name === 'QuotaExceededError' ||
            // Firefox
            e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            (storage && storage.length !== 0);
    }
}

/**
 * Returns a list of integers from start (inclusive) to stop (exclusive),
 * incremented (or decremented) by step, exclusive. Note that ranges that stop
 * before they start are considered to be zero-length instead of negative - if
 * you'd like a negative range, use a negative step.
 * @param {number} start The number to start with
 * @param {number} stop The number to end with
 * @param {number} step The number to increment with
 */
export const range = (start, stop, step) => {
    if (stop == null) {
      stop = start || 0;
      start = 0;
    }
    if (!step) {
      step = stop < start ? -1 : 1;
    }

    var length = Math.max(Math.ceil((stop - start) / step), 0);
    var range = Array(length);

    for (var idx = 0; idx < length; idx++, start += step) {
      range[idx] = start;
    }

    return range;
};

/**
 * Serialize inputs from a form element into a url-encoded string
 * @param {Node} form
 */
export const serializeForm = (form) =>  {

	// Setup our serialized data
	var serialized = [];

	// Loop through each field in the form
	for (var i = 0; i < form.elements.length; i++) {

		var field = form.elements[i];

		// Don't serialize fields without a name, submits, buttons, file and reset inputs, and disabled fields
		if (!field.name || field.disabled || field.type === 'file' || field.type === 'reset' || field.type === 'submit' || field.type === 'button') continue;

		// If a multi-select, get all selections
		if (field.type === 'select-multiple') {
			for (var n = 0; n < field.options.length; n++) {
				if (!field.options[n].selected) continue;
				serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.options[n].value));
			}
		}

		// Convert field data to a query string
		else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
			serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.value));
		}
	}

	return serialized.join('&');

};

/**
 * Serialize a plain object into a url-encoded string
 * @param {Object} obj
 */
export const serializeObject = (obj) =>  {

	// Setup our serialized data
	var serialized = [];

	// Loop through each field in the obj
    Object.keys(obj).forEach( key => {
        serialized.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]))
    } )

	return serialized.join('&');

};

/**
 * Limit the frequency with which a given function can be invoked
 * @param {Function} func
 * @param {Integer} wait
 * @param {Object} options
 */
export const throttle = (func, wait, options) => {
    var timeout, context, args, result;
    var previous = 0;
    if (!options) options = {};

    var later = function() {
        previous = options.leading === false ? 0 : new Date().getTime();
        timeout = null;
        result = func.apply(context, args);
        if ( ! timeout) context = args = null;
    };

    var throttled = function() {
    var now = new Date().getTime();
    if (!previous && options.leading === false) previous = now;
    var remaining = wait - (now - previous);
    context = this;
    args = arguments;
    if (remaining <= 0 || remaining > wait) {
        if (timeout) {
        clearTimeout(timeout);
        timeout = null;
        }
        previous = now;
        result = func.apply(context, args);
        if (!timeout) context = args = null;
    } else if (!timeout && options.trailing !== false) {
        timeout = setTimeout(later, remaining);
    }
    return result;
    };

    throttled.cancel = function() {
    clearTimeout(timeout);
    previous = 0;
    timeout = context = args = null;
    };

    return throttled;
};


/**
 * Returns a function, that, as long as it continues to be invoked, will not be
 * triggered. The function will be called after it stops being called for N
 * milliseconds. If immediate is passed, trigger the function on the leading edge,
 * instead of the trailing.
 * https://underscorejs.org/docs/underscore.html#section-86
 * @param {Function} func
 * @param {Integer} wait
 * @param {Boolean} immediate
 */
export const debounce = (func, wait, immediate) => {
    var timeout, result;

    var later = function(context, args) {
      timeout = null;
      if (args) result = func.apply(context, args);
    };

    var debounced = __restArguments(function(args) {
      if (timeout) clearTimeout(timeout);
      if (immediate) {
        var callNow = !timeout;
        timeout = setTimeout(later, wait);
        if (callNow) result = func.apply(this, args);
      } else {
        timeout = delay(later, wait, this, args);
      }

      return result;
    });

    debounced.cancel = function() {
      clearTimeout(timeout);
      timeout = null;
    };

    return debounced;
};


/**
 * Delays a function for the given number of milliseconds, and then calls it with
 * the arguments supplied.
 * https://underscorejs.org/docs/underscore.html#section-83
 */
export const delay = __restArguments(function(func, wait, args) {
    return setTimeout(function() {
    	return func.apply(null, args);
    }, wait);
});


/**
 * Returns a deep copy of the given object
 * @param {object} object
 */
export const deepCopyObject = (object) => {

    if ( typeof object !== 'object' ) {
        console.log(`deepCopyObject() expects an object as argument. ${typeof object} given.`)
        console.trace()
        return []
    }

    return JSON.parse( JSON.stringify( object ) )

}


/**
 * Sort objects in an array in DESCENDING order
 * @param {string} by | key of the object to compare
 */
export const sortDESC = (by) => {
    return function(a,b) {
        if ( ! a[by] || a[by] < b[by]) {
            return 1
        }
        if (a[by] > b[by]) {
            return -1
        }
        return 0
    }
}


/**
 * Sort objects in an array in ASCENDING order
 * @param {string} by | key of the object to compare
 */
export const sortASC = (by) => {
    return function(a,b) {
        if ( ! a[by] || a[by] > b[by]) {
            return 1
        }
        if (a[by] < b[by]) {
            return -1
        }
        return 0
    }
}


/**
 * From a given number (num), get the nearest number that is evenly divisible by another number (divisor)
 * @param {number} num | initial number
 * @param {number} divisor | number that result should be evenly divisible by
 */
export const nearestDivisor = (num, divisor) => {
    return Math.round( num / divisor ) * divisor
}


/**
 * Get the URL query parameters as an object
 * @returns {object}
 */
export const getQueryObject = (search) => {
    search = search || window.location.search
	const queryParams = search.length ? search.substr(1).split('&') : []
	let o = {}
	queryParams.forEach( param => {
		const key = param.split('=')[0]
		const value = param.split('=')[1]
		o[key] = decodeURIComponent(value)
	} )
	return o
}


/**
 * Construct data about a hex value
 * @param {string} hex | hex string beginning with a '#' character
 */
export const getColorData = ( hex ) => {
    hex = hex.substring(1);
	var colorObj = {}

    /* Get the RGB values to calculate the Hue. */
    var r = parseInt(hex.substring(0, 2), 16) / 255;
    var g = parseInt(hex.substring(2, 4), 16) / 255;
    var b = parseInt(hex.substring(4, 6), 16) / 255;

    /* Getting the Max and Min values for Chroma. */
    var max = Math.max.apply(Math, [r, g, b]);
    var min = Math.min.apply(Math, [r, g, b]);

    /* Variables for HSV value of hex color. */
    var chr = max - min;
    var hue = 0;
    var val = max;
    var sat = 0;

    if (val > 0) {
        /* Calculate Saturation only if Value isn't 0. */
        sat = chr / val;
        if (sat > 0) {
            if (r === max) {
                hue = 60 * (((g - min) - (b - min)) / chr);
                if (hue < 0) {
                    hue += 360;
                }
            } else if (g === max) {
                hue = 120 + 60 * (((b - min) - (r - min)) / chr);
            } else if (b === max) {
                hue = 240 + 60 * (((r - min) - (g - min)) / chr);
            }
        }
    }

    colorObj.chroma = chr;
    colorObj.hue = hue;
    colorObj.sat = sat;
    colorObj.val = val;
    colorObj.luma = 0.3 * r + 0.59 * g + 0.11 * b;
    colorObj.red = parseInt(hex.substring(0, 2), 16);
    colorObj.green = parseInt(hex.substring(2, 4), 16);
    colorObj.blue = parseInt(hex.substring(4, 6), 16);

    return colorObj;
}


/**
 * Some functions take a variable number of arguments, or a few expected arguments
 * at the beginning and then a variable number of values to operate on. This
 * helper accumulates all remaining arguments past the function’s argument length
 * (or an explicit startIndex), into an array that becomes the last argument.
 * Similar to ES6’s “rest parameter”.
 * https://underscorejs.org/docs/underscore.html#section-17
 * @param {function} func
 * @param {integer} startIndex
 */
function __restArguments(func, startIndex) {
    startIndex = startIndex == null ? func.length - 1 : +startIndex;
    return function() {
      var length = Math.max(arguments.length - startIndex, 0),
          rest = Array(length),
          index = 0;
      for (; index < length; index++) {
        rest[index] = arguments[index + startIndex];
      }
      switch (startIndex) {
        case 0: return func.call(this, rest);
        case 1: return func.call(this, arguments[0], rest);
        case 2: return func.call(this, arguments[0], arguments[1], rest);
        default:
      }
      var args = Array(startIndex + 1);
      for (index = 0; index < startIndex; index++) {
        args[index] = arguments[index];
      }
      args[startIndex] = rest;
      return func.apply(this, args);
    };
}
